import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../../../components/Button";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import * as Fonts from "../../../../../../components/Fonts";
import { LoadingModal } from "../../../../../../components/LoadingModal";
import { SubHeader } from "../../../../../../components/SubHeader";
import constants from "../../../../../../constants";
import { useAppState } from "../../../../../../contexts/App";
import { MailChimpStorage } from "../../../../../../model/site";
import { api, MailChimpAudienceList } from "../../../../../../services/api";
import { navigationState } from "../../../Sections/navigationState";
import StorageIntegrationServiceInfo from "../../components/StorageIntegrationServiceInfo";
import { FormNavigationState } from "../../formNavigationState";
import MailChimpIcon from "../../icons/MailChimp";
import { authenticationFlow } from "../../services/authentication-flow";
import { Card, CardFooter, Container, RemoveLink, Section } from "../styles";
import { AudienceSelectList } from "./components/AudienceSelectList";

const getTokenUrl = `https://login.mailchimp.com/oauth2/authorize?\
response_type=code&client_id=${constants.mailChimpClientId}\
&redirect_uri=${constants.site}/callback.html`;

export const MailChimpStoragePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const editingSiteId = useAppState().appState?.editingSiteId as string;

  const [audienceLists, setAudienceLists] = useState<MailChimpAudienceList[]>([]);
  const [isLoadingAudienceLists, setLoadingAudienceLists] = useState(false);
  const [isCreatingConfiguration, setCreatingConfiguration] = useState(false);
  const [isShowDisconnectConfirmModal, setShowDisconnectConfirmModal] = useState(false);

  const formNavigationState = navigationState.get() as FormNavigationState;

  const section = formNavigationState.section;

  const [mailChimp, setMailChimp] = useState({
    ...section.storage.mailChimp,
  });

  const hasUnsavedChanges =
    (mailChimp.active && !section.storage.mailChimp.active) ||
    (!!mailChimp.audienceList && mailChimp.audienceList.id !== section.storage?.mailChimp?.audienceList?.id);

  const startConnectFlow = async () => {
    if (isCreatingConfiguration) {
      return;
    }
    const code = await authenticationFlow.getCodeFromModal({
      url: getTokenUrl,
      target: "mailchimp",
    });
    setCreatingConfiguration(true);

    try {
      const { id } = await api.createMailChimpFormConnection(editingSiteId, code);
      const lists = await loadAudienceLists(id);
      setMailChimp({
        ...mailChimp,
        active: true,
        connectionId: id,
        audienceList: lists[0],
      });
    } finally {
      setCreatingConfiguration(false);
    }
  };

  const disconnect = () => setShowDisconnectConfirmModal(true);

  const confirmDisconnect = () => {
    const mailChimpCopy = { active: false };

    setMailChimp(mailChimpCopy);
    updateState(mailChimpCopy);
    setShowDisconnectConfirmModal(false);
  };

  const updateState = (mailChimp: MailChimpStorage) => {
    const state: FormNavigationState = {
      ...formNavigationState,
      section: {
        ...section,
        storage: {
          ...section.storage,
          mailChimp,
        },
      },
    };

    navigationState.set(state);
  };

  const loadAudienceLists = async (connectionId: string) => {
    setLoadingAudienceLists(true);

    const lists = await api.getAudienceListsMailChimpFormConnection(editingSiteId, connectionId);

    setLoadingAudienceLists(false);
    setAudienceLists(lists);

    return lists;
  };

  // Saving hands the choice back to the form screen, which stores it when the form is saved
  const saveHandler = () => {
    if (!mailChimp.active) {
      return;
    }
    updateState(mailChimp);
    navigate(-1);
  };

  const discardChanges = () =>
    setMailChimp(current => ({ ...current, audienceList: section.storage.mailChimp.audienceList }));

  const prefix = "pages.editor.form.form.storage";

  useEffect(() => {
    if (mailChimp.active) {
      loadAudienceLists(mailChimp.connectionId as string);
    }
    return () => authenticationFlow.reset();
  }, []);

  return (
    <>
      {(isLoadingAudienceLists || isCreatingConfiguration) && <LoadingModal />}
      <Container>
        <SubHeader
          title="Mailchimp"
          crumbs={[
            { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
            { label: t("pages.editor.form.header.title"), to: "/editor/form" },
          ]}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        <Section>
          <StorageIntegrationServiceInfo
            name="Mailchimp"
            LogoIcon={MailChimpIcon}
            logoContainerColor="#FFE01B"
            info={t(`${prefix}.mailChimp.info`)}
            active={mailChimp.active}
            statusLabel={mailChimp.active ? t(`${prefix}.status.connected`) : t(`${prefix}.status.disconnected`)}
          />
        </Section>

        <Section>
          <Fonts.Label>{t(`${prefix}.sections.setup`)}</Fonts.Label>
          <Card>
            {mailChimp.active ? (
              <AudienceSelectList
                value={mailChimp.audienceList}
                isLoadingList={isLoadingAudienceLists}
                audienceLists={audienceLists}
                onChange={audienceList => setMailChimp({ ...mailChimp, audienceList })}
              />
            ) : (
              <p className="lead">{t(`${prefix}.mailChimp.connectDescription`)}</p>
            )}
            <CardFooter>
              {mailChimp.active ? (
                <>
                  {hasUnsavedChanges && section.storage.mailChimp.active && (
                    <LinkButton onClick={discardChanges}>{t("pages.settings.unsavedBar.discard")}</LinkButton>
                  )}
                  <Button type="INFO" size="MD" onClick={saveHandler}>
                    {t("actions.save")}
                  </Button>
                </>
              ) : (
                <Button type="INFO" size="MD" onClick={startConnectFlow}>
                  {t(`${prefix}.mailChimp.connectButton`)}
                </Button>
              )}
            </CardFooter>
          </Card>
        </Section>

        {mailChimp.active && (
          <RemoveLink type="button" onClick={disconnect}>
            {t(`${prefix}.mailChimp.actions.disconnect.link`)}
          </RemoveLink>
        )}
      </Container>
      {isShowDisconnectConfirmModal && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.form.form.storage.mailChimp.actions.disconnect.confirmModal.title")}
          cancelTitle={t("pages.editor.form.form.storage.mailChimp.actions.disconnect.confirmModal.cancelButton")}
          confirmButtonTitle={t(
            "pages.editor.form.form.storage.mailChimp.actions.disconnect.confirmModal.confirmButton"
          )}
          onConfirm={confirmDisconnect}
          onCancel={() => setShowDisconnectConfirmModal(false)}
        />
      )}
    </>
  );
};
